/**
 * This file is the entry point for the React app, it sets up the root
 * element and renders the App component to the DOM.
 *
 * It is included in `src/index.html`.
 */

import Clarity from '@microsoft/clarity'
import * as Sentry from '@sentry/react'
import {StrictMode} from 'react'
import {createRoot} from 'react-dom/client'
import {App} from './App'
import {ErrorFallback} from './components/ErrorFallback'
import {API_BASE_URL} from './lib/config'

if (import.meta.env.DEV) {
	for (const link of Array.from(
		document.querySelectorAll<HTMLLinkElement>('link[rel~="icon"]'),
	)) {
		link.remove()
	}

	const devFavicon = document.createElement('link')
	devFavicon.rel = 'icon'
	devFavicon.type = 'image/svg+xml'
	devFavicon.href = '/favicon-dev.svg'
	document.head.append(devFavicon)
}

Sentry.init({
	dsn: import.meta.env.VITE_SENTRY_DSN,
	environment: import.meta.env.VITE_APP_ENV || import.meta.env.MODE,
	enabled: import.meta.env.MODE === 'production',
	// Git SHA of the deploy — baked in by deploy-ui.yml via the VITE_RELEASE
	// build-arg. Must match the release name the Sentry vite plugin uploads
	// sourcemaps under (vite.config.ts) so stack traces resolve per-deploy.
	release: import.meta.env.VITE_RELEASE,
	// Intentionally enabled for user support and debugging
	sendDefaultPii: true,
	// Full trace capture (one org, <10 users — see the API-side rationale in
	// packages/api/src/lib/sentry.ts). Lower both sides together.
	tracesSampleRate: 1.0,
	// Attach sentry-trace/baggage headers on API calls so a slow board load
	// shows browser span + linked API span in one trace. The API's CORS
	// allowHeaders must keep listing both headers or preflights fail.
	tracePropagationTargets: [
		'localhost',
		...(API_BASE_URL ? [API_BASE_URL] : []),
	],
	// Errors-only replay capture: don't record normal sessions, but flush the
	// buffered replay whenever an error occurs.
	replaysSessionSampleRate: 0,
	replaysOnErrorSampleRate: 1.0,
	integrations: [
		// Performance tracing — instruments fetch/XHR, route changes, and web
		// vitals. Sampling is governed by tracesSampleRate above.
		Sentry.browserTracingIntegration(),
		// Global "Contact us" channel (feedback / bug reports / inquiries).
		// We render our own sidebar entry (see ContactMenuItem) and attach it via
		// getFeedback().attachTo(), so Sentry's own button is disabled. Free on
		// the Developer plan; submissions land in Sentry → User Feedback. Prefill
		// comes from Sentry.setUser() set in __root.tsx.
		Sentry.feedbackIntegration({
			autoInject: false,
			colorScheme: 'system',
			showBranding: false,
			showName: true,
			showEmail: true,
			enableScreenshot: true,
			useSentryUser: {email: 'email', name: 'username'},
			formTitle: 'Contact us',
			submitButtonLabel: 'Send',
			messagePlaceholder:
				"Share feedback, report a bug, or ask a question — we'll get back to you.",
			successMessageText: "Thanks! We'll be in touch.",
			// Flat aesthetic — no decorative elevation on the modal.
			themeLight: {boxShadow: 'none'},
			themeDark: {boxShadow: 'none'},
		}),
		// Session Replay — DOM recording of the lead-up to an error. Buffered
		// (errors-only, see replaysOnErrorSampleRate above) so we only spend quota
		// on sessions that actually fail. maskAllText + blockAllMedia are Sentry
		// defaults; set explicitly here to make the privacy posture legible — we
		// record layout and interactions, never customer permit data / names /
		// emails.
		Sentry.replayIntegration({
			maskAllText: true,
			blockAllMedia: true,
		}),
	],
})

// Initialize Microsoft Clarity (production only)
if (
	import.meta.env.MODE === 'production' &&
	import.meta.env.VITE_CLARITY_PROJECT_ID
) {
	Clarity.init(import.meta.env.VITE_CLARITY_PROJECT_ID)
}

const elem = document.getElementById('root')
if (!elem) {
	throw new Error('Root element not found')
}
const app = (
	<StrictMode>
		<Sentry.ErrorBoundary fallback={<ErrorFallback />}>
			<App />
		</Sentry.ErrorBoundary>
	</StrictMode>
)

if (import.meta.hot) {
	// With hot module reloading, `import.meta.hot.data` is persisted.
	let root = import.meta.hot.data.root

	if (!root) {
		root = createRoot(elem)
		import.meta.hot.data.root = root
	}
	root.render(app)
} else {
	// The hot module reloading API is not available in production.
	createRoot(elem).render(app)
}
